/**
 * UserRlsTab — RLS bindings management tab for user edit drawer.
 *
 * Stable-key RLS editor for SH.
 * Editable columns:
 * - Филиал (`fil_code`)
 * - Подразделение (`podr`)
 * - Служба (`serv_code` -> runtime `srv_name`)
 * - Отделение (`id_otd`)
 *
 * Source: design-rls-admin-ui-2026-04-12.md section 3
 */
import { useState, useEffect, useMemo, useCallback } from 'react'
import { Button, Select, Space, Tag, Popconfirm, message, Spin, Alert, Row, Col, Typography, Card, Table, Tooltip } from 'antd'
import { PlusOutlined, SaveOutlined, UndoOutlined, SearchOutlined } from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import { useUserRlsBindings, useBulkReplaceRls, useRlsOptions, useEffectiveRlsRows, usePreviewEffectiveRls } from '@/hooks/useAccessGroups'
import type { EffectiveRlsRow, RlsBindingItem, RlsBindingType, RlsOptionItem, RlsOptionsParams } from '@/api/accessGroups'

const { Text } = Typography

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const BINDING_TYPE_LABELS: Record<RlsBindingType, string> = {
  fil: 'Филиал',
  podr: 'Подразделение',
  srv: 'Служба',
  otd: 'Отделение',
}

const BINDING_TYPE_COLORS: Record<RlsBindingType, string> = {
  fil: 'blue',
  podr: 'purple',
  srv: 'green',
  otd: 'orange',
}

// ---------------------------------------------------------------------------
// Sub-component: single column for fil/podr/srv (independent)
// ---------------------------------------------------------------------------

interface BindingColumnProps {
  type: RlsBindingType
  bindings: string[]
  lpuCode: string
  optionFilters?: Pick<RlsOptionsParams, 'fil' | 'podr' | 'srv' | 'usl_ok' | 'profil_mp' | 'profil_k'>
  onAdd: (code: string) => void
  onRemove: (code: string) => void
  disabled?: boolean
  note?: string
}

function BindingColumn({ type, bindings, lpuCode, optionFilters, onAdd, onRemove, disabled = false, note }: BindingColumnProps) {
  const params: RlsOptionsParams = { type, lpu_code: lpuCode, ...optionFilters }
  const allParams: RlsOptionsParams = { type, lpu_code: lpuCode }
  const { data: options, isLoading } = useRlsOptions(params)
  const { data: allOptions } = useRlsOptions(allParams)
  const optionLabelMap = useMemo(
    () => new Map((allOptions || []).map((o) => [o.code, o.code === o.name ? o.name : `${o.name} (${o.code})`])),
    [allOptions]
  )

  const availableOptions = useMemo(() => {
    if (!options) return []
    const existingSet = new Set(bindings)
    return options
      .filter((o) => !existingSet.has(o.code))
      .map((o) => ({ value: o.code, label: o.code === o.name ? o.name : `${o.name} (${o.code})` }))
  }, [options, bindings])

  return (
    <div>
      <Text strong style={{ display: 'block', marginBottom: 8, color: '#595959' }}>
        {BINDING_TYPE_LABELS[type]}
      </Text>
      <div style={{ minHeight: 32, marginBottom: 8 }}>
        {bindings.length === 0 ? (
          <Text type="secondary" style={{ fontSize: 13 }}>Нет привязок</Text>
        ) : (
          bindings.map((code) => (
            <Tooltip key={code} title={optionLabelMap.get(code) ?? code}>
              <Tag
                color={BINDING_TYPE_COLORS[type]}
                closable
                onClose={(e) => { e.preventDefault(); onRemove(code) }}
                style={{ marginBottom: 4 }}
              >
                {code}
              </Tag>
            </Tooltip>
          ))
        )}
      </div>
      {note && (
        <Text type="secondary" style={{ display: 'block', marginBottom: 8, fontSize: 12 }}>
          {note}
        </Text>
      )}
      <Select
        showSearch
        placeholder={disabled ? 'Недоступно' : 'Добавить...'}
        loading={isLoading && !disabled}
        options={availableOptions}
        value={undefined}
        onChange={(val) => { if (val) onAdd(val) }}
        style={{ width: '100%' }}
        disabled={disabled}
        filterOption={(input, option) =>
          (option?.label as string)?.toLowerCase().includes(input.toLowerCase()) ?? false
        }
        notFoundContent={disabled ? 'Отключено' : (isLoading ? <Spin size="small" /> : 'Нет данных')}
        suffixIcon={<PlusOutlined />}
      />
      {bindings.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <Text type="secondary" style={{ display: 'block', marginBottom: 4, fontSize: 12 }}>
            Расшифровка
          </Text>
          <Space direction="vertical" size={2} style={{ width: '100%' }}>
            {bindings.map((code) => (
              <Text key={code} style={{ fontSize: 12 }}>
                <Text code>{code}</Text>{' '}
                <Text type="secondary">{optionLabelMap.get(code) ?? '—'}</Text>
              </Text>
            ))}
          </Space>
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Sub-component: Отделение column with cascading filter
// ---------------------------------------------------------------------------

interface OtdColumnProps {
  bindings: string[]
  lpuCode: string
  optionFilters: Pick<RlsOptionsParams, 'fil' | 'podr' | 'srv' | 'usl_ok' | 'profil_mp' | 'profil_k'>
  onAdd: (code: string) => void
  onRemove: (code: string) => void
}

function OtdColumn({ bindings, lpuCode, optionFilters, onAdd, onRemove }: OtdColumnProps) {
  const otdParams: RlsOptionsParams = useMemo(() => ({
    type: 'otd',
    lpu_code: lpuCode,
    ...optionFilters,
  }), [lpuCode, optionFilters])
  const allOtdParams: RlsOptionsParams = useMemo(() => ({
    type: 'otd',
    lpu_code: lpuCode,
  }), [lpuCode])

  const { data: otdOptions, isLoading } = useRlsOptions(otdParams)
  const { data: allOtdOptions } = useRlsOptions(allOtdParams)

  const availableOtd = useMemo(() => {
    if (!otdOptions) return []
    const existingSet = new Set(bindings)
    return otdOptions
      .filter((o) => !existingSet.has(o.code))
      .map((o) => ({ value: o.code, label: `${o.name} (${o.code})` }))
  }, [otdOptions, bindings])

  const optionLabelMap = useMemo(
    () => new Map((allOtdOptions || []).map((o) => [o.code, `${o.name} (${o.code})`])),
    [allOtdOptions]
  )

  return (
    <div>
      <Text strong style={{ display: 'block', marginBottom: 8, color: '#595959' }}>
        {BINDING_TYPE_LABELS.otd}
      </Text>
      <div style={{ minHeight: 32, marginBottom: 8 }}>
        {bindings.length === 0 ? (
          <Text type="secondary" style={{ fontSize: 13 }}>Нет привязок</Text>
        ) : (
          bindings.map((code) => (
            <Tooltip key={code} title={optionLabelMap.get(code) ?? code}>
              <Tag
                color={BINDING_TYPE_COLORS.otd}
                closable
                onClose={(e) => { e.preventDefault(); onRemove(code) }}
                style={{ marginBottom: 4 }}
              >
                {code}
              </Tag>
            </Tooltip>
          ))
        )}
      </div>

      <Select
        showSearch
        placeholder="Добавить отделение..."
        loading={isLoading}
        options={availableOtd}
        value={undefined}
        onChange={(val) => { if (val) onAdd(val) }}
        style={{ width: '100%' }}
        filterOption={(input, option) =>
          (option?.label as string)?.toLowerCase().includes(input.toLowerCase()) ?? false
        }
        notFoundContent={isLoading ? <Spin size="small" /> : 'Нет данных'}
        suffixIcon={<PlusOutlined />}
      />
      {bindings.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <Text type="secondary" style={{ display: 'block', marginBottom: 4, fontSize: 12 }}>
            Расшифровка
          </Text>
          <Space direction="vertical" size={2} style={{ width: '100%' }}>
            {bindings.map((code) => (
              <Text key={code} style={{ fontSize: 12 }}>
                <Text code>{code}</Text>{' '}
                <Text type="secondary">{optionLabelMap.get(code) ?? '—'}</Text>
              </Text>
            ))}
          </Space>
        </div>
      )}
    </div>
  )
}

interface LookupFilters {
  fil?: string
  podr?: string
  srv?: string
  usl_ok?: string
  profil_mp?: string
  profil_k?: string
}

interface LookupFilterBarProps {
  lpuCode: string
  filters: LookupFilters
  onChange: (next: LookupFilters) => void
}

function formatOptionLabel(option: RlsOptionItem): string {
  return option.code === option.name ? option.name : `${option.name} (${option.code})`
}

function LookupFilterBar({ lpuCode, filters, onChange }: LookupFilterBarProps) {
  const buildLookupParams = useCallback(
    (
      type: RlsOptionsParams['type'],
      exclude?: keyof LookupFilters
    ): RlsOptionsParams => ({
      type,
      lpu_code: lpuCode,
      ...(filters.fil && exclude !== 'fil' ? { fil: filters.fil } : {}),
      ...(filters.podr && exclude !== 'podr' ? { podr: filters.podr } : {}),
      ...(filters.srv && exclude !== 'srv' ? { srv: filters.srv } : {}),
      ...(filters.usl_ok && exclude !== 'usl_ok' ? { usl_ok: filters.usl_ok } : {}),
      ...(filters.profil_mp && exclude !== 'profil_mp' ? { profil_mp: filters.profil_mp } : {}),
      ...(filters.profil_k && exclude !== 'profil_k' ? { profil_k: filters.profil_k } : {}),
    }),
    [filters, lpuCode]
  )

  const filParams = useMemo(() => buildLookupParams('fil', 'fil'), [buildLookupParams])
  const podrParams = useMemo(() => buildLookupParams('podr', 'podr'), [buildLookupParams])
  const srvParams = useMemo(() => buildLookupParams('srv', 'srv'), [buildLookupParams])
  const uslOkParams = useMemo(() => buildLookupParams('usl_ok', 'usl_ok'), [buildLookupParams])
  const profilMpParams = useMemo(() => buildLookupParams('profil_mp', 'profil_mp'), [buildLookupParams])
  const profilKParams = useMemo(() => buildLookupParams('profil_k', 'profil_k'), [buildLookupParams])

  const { data: filOptions, isLoading: isFilLoading } = useRlsOptions(filParams)
  const { data: podrOptions, isLoading: isPodrLoading } = useRlsOptions(podrParams)
  const { data: srvOptions, isLoading: isSrvLoading } = useRlsOptions(srvParams)
  const { data: uslOkOptions, isLoading: isUslOkLoading } = useRlsOptions(uslOkParams)
  const { data: profilMpOptions, isLoading: isProfilMpLoading } = useRlsOptions(profilMpParams)
  const { data: profilKOptions, isLoading: isProfilKLoading } = useRlsOptions(profilKParams)

  const hasActiveFilters = Object.values(filters).some(Boolean)

  const toSelectOptions = useCallback(
    (options: RlsOptionItem[] | undefined) =>
      (options || []).map((option) => ({ value: option.code, label: formatOptionLabel(option) })),
    []
  )

  return (
    <Card size="small" title="Фильтры вариантов" style={{ marginBottom: 16 }}>
      <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
        Эти фильтры только сужают списки для выбора и не ограничивают уже выданные RLS-привязки.
        Профильные фильтры (`КС/ДС/АПП`, профиль МП, профиль койки) берутся из актуального `ref_bed_fund`.
      </Text>
      <Row gutter={[12, 12]} align="middle">
        <Col xs={24} md={12} xl={6}>
          <Select
            allowClear
            showSearch
            placeholder="Филиал"
            value={filters.fil}
            loading={isFilLoading}
            options={toSelectOptions(filOptions)}
            style={{ width: '100%' }}
            onChange={(fil) => onChange({ ...filters, fil, podr: undefined, srv: undefined })}
            filterOption={(input, option) =>
              (option?.label as string)?.toLowerCase().includes(input.toLowerCase()) ?? false
            }
          />
        </Col>
        <Col xs={24} md={12} xl={6}>
          <Select
            allowClear
            showSearch
            placeholder="Подразделение"
            value={filters.podr}
            loading={isPodrLoading}
            options={toSelectOptions(podrOptions)}
            style={{ width: '100%' }}
            onChange={(podr) => onChange({ ...filters, podr })}
            filterOption={(input, option) =>
              (option?.label as string)?.toLowerCase().includes(input.toLowerCase()) ?? false
            }
          />
        </Col>
        <Col xs={24} md={12} xl={6}>
          <Select
            allowClear
            showSearch
            placeholder="Служба"
            value={filters.srv}
            loading={isSrvLoading}
            options={toSelectOptions(srvOptions)}
            style={{ width: '100%' }}
            onChange={(srv) => onChange({ ...filters, srv })}
            filterOption={(input, option) =>
              (option?.label as string)?.toLowerCase().includes(input.toLowerCase()) ?? false
            }
          />
        </Col>
        <Col xs={24} md={12} xl={6}>
          <Select
            allowClear
            showSearch
            placeholder="КС / ДС / АПП"
            value={filters.usl_ok}
            loading={isUslOkLoading}
            options={toSelectOptions(uslOkOptions)}
            style={{ width: '100%' }}
            onChange={(usl_ok) => onChange({ ...filters, usl_ok })}
            filterOption={(input, option) =>
              (option?.label as string)?.toLowerCase().includes(input.toLowerCase()) ?? false
            }
          />
        </Col>
        <Col xs={24} lg={10}>
          <Select
            allowClear
            showSearch
            placeholder="Профиль МП"
            value={filters.profil_mp}
            loading={isProfilMpLoading}
            options={toSelectOptions(profilMpOptions)}
            style={{ width: '100%' }}
            onChange={(profil_mp) => onChange({ ...filters, profil_mp })}
            filterOption={(input, option) =>
              (option?.label as string)?.toLowerCase().includes(input.toLowerCase()) ?? false
            }
          />
        </Col>
        <Col xs={24} lg={10}>
          <Select
            allowClear
            showSearch
            placeholder="Профиль койки"
            value={filters.profil_k}
            loading={isProfilKLoading}
            options={toSelectOptions(profilKOptions)}
            style={{ width: '100%' }}
            onChange={(profil_k) => onChange({ ...filters, profil_k })}
            filterOption={(input, option) =>
              (option?.label as string)?.toLowerCase().includes(input.toLowerCase()) ?? false
            }
          />
        </Col>
        <Col xs={24} lg={4}>
          <Button block disabled={!hasActiveFilters} onClick={() => onChange({})}>
            Сбросить
          </Button>
        </Col>
      </Row>
    </Card>
  )
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

interface UserRlsTabProps {
  userId: string
  lpuCode: string | null | undefined
}

const PREVIEW_COLUMNS: ColumnsType<EffectiveRlsRow> = [
  {
    title: 'Уровень',
    dataIndex: 'access_level',
    key: 'access_level',
    width: 100,
    render: (value: EffectiveRlsRow['access_level']) => (
      <Tag color={BINDING_TYPE_COLORS[value]}>{BINDING_TYPE_LABELS[value]}</Tag>
    ),
  },
  {
    title: 'Филиал',
    key: 'fil',
    render: (_: unknown, row: EffectiveRlsRow) =>
      row.fil_code !== null || row.fil_name ? `${row.fil_name}${row.fil_code !== null ? ` (${row.fil_code})` : ''}` : '—',
  },
  {
    title: 'Служба',
    key: 'srv',
    render: (_: unknown, row: EffectiveRlsRow) =>
      row.srv_name ? `${row.srv_name}${row.srv_code !== null ? ` (${row.srv_code})` : ''}` : '—',
  },
  {
    title: 'Подразделение',
    key: 'podr',
    render: (_: unknown, row: EffectiveRlsRow) =>
      row.podr ? `${row.podr_name_short || '—'} (${row.podr})` : '—',
  },
  {
    title: 'Отделение',
    key: 'otd',
    render: (_: unknown, row: EffectiveRlsRow) =>
      row.id_otd ? `${row.name_otd || '—'} (${row.id_otd})` : '—',
  },
]

export default function UserRlsTab({ userId, lpuCode }: UserRlsTabProps) {
  const canLoadEffectiveState = Boolean(userId && lpuCode)
  const {
    data: serverBindings,
    isLoading,
    error: serverBindingsError,
  } = useUserRlsBindings(userId, canLoadEffectiveState)
  const { data: savedEffectiveRows, isLoading: isEffectiveRowsLoading, refetch: refetchEffectiveRows } = useEffectiveRlsRows(userId, canLoadEffectiveState)
  const bulkReplaceMutation = useBulkReplaceRls()
  const previewEffectiveMutation = usePreviewEffectiveRls()

  // ── Local editing state ──
  const [localBindings, setLocalBindings] = useState<RlsBindingItem[]>([])
  const [initialized, setInitialized] = useState(false)
  const [previewRows, setPreviewRows] = useState<EffectiveRlsRow[] | null>(null)
  const [previewMode, setPreviewMode] = useState<'saved' | 'draft'>('saved')
  const [lookupFilters, setLookupFilters] = useState<LookupFilters>({})

  const serverBindingItems = useMemo(
    () =>
      (serverBindings || []).map((b) => ({
        binding_type: b.binding_type as RlsBindingType,
        object_code: b.object_code,
      })),
    [serverBindings]
  )

  // Reset when userId changes
  useEffect(() => {
    setInitialized(false)
    setLocalBindings([])
    setLookupFilters({})
    setPreviewRows(null)
    setPreviewMode('saved')
  }, [userId])

  const draftBindings = initialized ? localBindings : serverBindingItems

  // ── Dirty check ──
  const isDirty = useMemo(() => {
    const serverSet = new Set(serverBindingItems.map((b) => `${b.binding_type}:${b.object_code}`))
    const localSet = new Set(draftBindings.map((b) => `${b.binding_type}:${b.object_code}`))
    if (serverSet.size !== localSet.size) return true
    for (const item of serverSet) {
      if (!localSet.has(item)) return true
    }
    return false
  }, [draftBindings, serverBindingItems])

  // ── Grouped bindings ──
  const groupedBindings = useMemo(() => {
    const groups: Record<RlsBindingType, string[]> = {
      fil: [],
      podr: [],
      srv: [],
      otd: [],
    }
    draftBindings.forEach((b) => {
      if (groups[b.binding_type as RlsBindingType]) {
        groups[b.binding_type as RlsBindingType].push(b.object_code)
      }
    })
    return groups
  }, [draftBindings])

  // ── Handlers ──
  const handleAdd = useCallback((type: RlsBindingType, code: string) => {
    if (draftBindings.some((b) => b.binding_type === type && b.object_code === code)) {
      message.warning('Такая привязка уже существует')
      return
    }
    setLocalBindings([...draftBindings, { binding_type: type, object_code: code }])
    setInitialized(true)
  }, [draftBindings])

  const handleRemove = useCallback((type: RlsBindingType, code: string) => {
    setLocalBindings(
      draftBindings.filter((b) => !(b.binding_type === type && b.object_code === code))
    )
    setInitialized(true)
  }, [draftBindings])

  const handleReset = useCallback(() => {
    setLocalBindings([])
    setInitialized(false)
    setPreviewRows(null)
    setPreviewMode('saved')
  }, [])

  const handleSave = async () => {
    await bulkReplaceMutation.mutateAsync({
      userId,
      bindings: { bindings: draftBindings },
    })
    setInitialized(false)
    setLocalBindings([])
    setPreviewRows(null)
    setPreviewMode('saved')
    void refetchEffectiveRows()
  }

  const handlePreview = async () => {
    const rows = await previewEffectiveMutation.mutateAsync({
      userId,
      bindings: { bindings: draftBindings },
    })
    setPreviewRows(rows)
    setPreviewMode('draft')
  }

  // ── Render ──
  if (isLoading) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Spin />
      </div>
    )
  }

  if (!lpuCode) {
    return <Alert type="warning" message="Организация пользователя не определена" />
  }

  if (serverBindingsError) {
    return (
      <Alert
        type="error"
        message="Не удалось загрузить RLS-привязки"
        description="Сервер не вернул текущее состояние RLS. Обновите страницу или проверьте backend-логи."
      />
    )
  }

  return (
    <div>
      <LookupFilterBar lpuCode={lpuCode} filters={lookupFilters} onChange={setLookupFilters} />

      <Row gutter={16}>
        <Col span={6}>
          <BindingColumn
            type="fil"
            bindings={groupedBindings.fil}
            lpuCode={lpuCode}
            optionFilters={lookupFilters}
            onAdd={(code) => handleAdd('fil', code)}
            onRemove={(code) => handleRemove('fil', code)}
          />
        </Col>

        <Col span={6}>
          <BindingColumn
            type="podr"
            bindings={groupedBindings.podr}
            lpuCode={lpuCode}
            optionFilters={lookupFilters}
            onAdd={(code) => handleAdd('podr', code)}
            onRemove={(code) => handleRemove('podr', code)}
            note="Используется стабильный код подразделения `podr`; label берется из `podr_name_short`."
          />
        </Col>

        <Col span={6}>
          <BindingColumn
            type="srv"
            bindings={groupedBindings.srv}
            lpuCode={lpuCode}
            optionFilters={lookupFilters}
            onAdd={(code) => handleAdd('srv', code)}
            onRemove={(code) => handleRemove('srv', code)}
            note="В UI по возможности хранится код службы; если directory `ref_services` отсутствует, используется legacy `serv_name`."
          />
        </Col>

        {/* Отделение column with lookup filters */}
        <Col span={6}>
          <OtdColumn
            bindings={groupedBindings.otd}
            lpuCode={lpuCode}
            optionFilters={lookupFilters}
            onAdd={(code) => handleAdd('otd', code)}
            onRemove={(code) => handleRemove('otd', code)}
          />
        </Col>
      </Row>

      {/* Unsaved changes warning */}
      {isDirty && (
        <Alert
          type="warning"
          showIcon
          style={{ marginTop: 16, marginBottom: 12 }}
          message="Есть несохраненные изменения"
        />
      )}

      {/* Save / Reset buttons */}
      <Space style={{ marginTop: 16 }}>
        <Popconfirm
          title="Сохранить RLS-привязки?"
          description="Все привязки будут заменены. Синхронизация с ClickHouse произойдет автоматически."
          onConfirm={handleSave}
          okText="Сохранить"
          cancelText="Отмена"
          disabled={!isDirty}
        >
          <Button
            type="primary"
            icon={<SaveOutlined />}
            loading={bulkReplaceMutation.isPending}
            disabled={!isDirty}
          >
            Сохранить привязки
          </Button>
        </Popconfirm>
        <Button
          icon={<UndoOutlined />}
          onClick={handleReset}
          disabled={!isDirty}
        >
          Сбросить
        </Button>
        <Button
          icon={<SearchOutlined />}
          onClick={() => { void handlePreview() }}
          loading={previewEffectiveMutation.isPending}
          disabled={!initialized}
        >
          Предпросмотр SH RLS
        </Button>
      </Space>

      <Card
        size="small"
        title={previewMode === 'draft' ? 'Effective SH RLS: текущий черновик' : 'Effective SH RLS: сохранённое состояние'}
        style={{ marginTop: 16 }}
      >
        {previewMode === 'saved' && isEffectiveRowsLoading ? (
          <Spin size="small" />
        ) : (
          <>
            <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
              Показывает строки, которые используются/будут использоваться в SH `mdc01_{'{code}'}.rls_access`.
            </Text>
            <Table<EffectiveRlsRow>
              size="small"
              rowKey={(row) => `${row.access_level}:${row.fil_code ?? ''}:${row.podr ?? ''}:${row.srv_code ?? row.srv_name}:${row.id_otd}`}
              columns={PREVIEW_COLUMNS}
              dataSource={previewMode === 'draft' ? (previewRows ?? []) : (savedEffectiveRows ?? [])}
              pagination={false}
              locale={{
                emptyText: 'Нет effective SH rows. Это нормально для full-access / wildcard сценариев.',
              }}
            />
          </>
        )}
      </Card>
    </div>
  )
}
